/* eslint-disable @typescript-eslint/no-unsafe-call */
import React, { useState } from 'react';
import Immutable from 'immutable';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { UI_OPERATION_STATUS_REQUEST } from '@uncinc/react-drupal-core';

import { BuddiesFormValues } from '../../../components/Forms/BuddiesForm/types';
import Page from '../../../containers/Page';
import useClasses from '../../../hooks/useClasses';
import useOperationStatus from '../../../hooks/useOperationStatus';
import usePopup from '../../../hooks/usePopup';
import useToast from '../../../hooks/useToast';
import useTypedDispatch from '../../../hooks/useTypedDispatch';
import { userAddBuddy, userDeleteBuddy } from '../../../store/actions/User';
import { FieldBuddy, UserState } from '../../../types';
import {
  BuddiesForm,
  Button,
  Main,
  Popup,
  Table,
} from '../../../components';

import './style.scss';

const PageAccountBuddies = () => {
  const dispatch = useTypedDispatch();
  const toast = useToast();
  const { t } = useTranslation();
  const { buildClass } = useClasses('PageAccountBuddies');
  const deletePopup = usePopup('delete-buddy');
  const operationStatusDeleteBuddy = useOperationStatus('user', 'delete_buddy');

  const [markedBuddyId, setMarkedBuddyId] = useState<number>();

  const buddies = useSelector<UserState, FieldBuddy[]>((state) => {
    const uid = state.getIn(['login', 'uid'], null);
    return state.getIn(['user', uid, 'field_buddies'], Immutable.List()).toJS();
  });

  const translationBase = 'pages.account.buddies';

  function handleSubmit({ name, email }: BuddiesFormValues) {
    async function addBuddy() {
      await dispatch(userAddBuddy(name, email));
    }

    addBuddy()
      .then(() => {
        toast.show(t(`${translationBase}.add.success_message`, { name }), 'success');
      })
      .catch((error) => {
        toast.show(t(`${translationBase}.add.error_message`, { name }), 'error');
        console.error(error);
      });
  }

  function handleDelete(buddyId: number) {
    if (!buddyId) {
      return;
    }

    async function deleteBuddy() {
      await dispatch(userDeleteBuddy(buddyId));
    }

    deleteBuddy()
      .then(() => {
        toast.show(t(`${translationBase}.delete.success_message`), 'success');
        setMarkedBuddyId(null);
      })
      .catch((error) => {
        toast.show(t(`${translationBase}.delete.error_message`), 'error');
        setMarkedBuddyId(null);
        console.error(error);
      });
  }

  return (
    <Page className={buildClass()} title={t(`${translationBase}.title`)}>
      <Main className="col-md-8 col-lg-6 offset-md-2 offset-lg-1 py-8 py-lg-12">
        <div className={buildClass('title')}>
          <h1>{t(`${translationBase}.title`)}</h1>
        </div>

        <div className={buildClass('body')}>
          <p>{t(`${translationBase}.body`)}</p>
        </div>

        {buddies && buddies.length > 0 && (
          <Table className={buildClass('overview')}>
            {buddies.map((buddy) => {
              const id = buddy.entity.id[0].value;
              const inviteAccepted = buddy.entity.status[0].value;
              const name = buddy.entity.name[0].value;
              const email = buddy.entity.field_email[0].value;

              return (
                <Table.Row key={`buddy:${id}`} className={buildClass('buddy')}>
                  <Table.Cell className={buildClass('buddy__credentials')}>
                    <strong className={buildClass('buddy__name')}>{name}</strong>
                    <div className={buildClass('buddy__email')}>{email}</div>
                  </Table.Cell>

                  <Table.Cell className={buildClass('buddy__status')}>
                    {t(`${translationBase}.status.${inviteAccepted ? 'accepted' : 'sent'}`)}
                  </Table.Cell>

                  <Table.Cell className={buildClass('buddy__remove-button')}>
                    <Button
                      iconOnly
                      icon="cross"
                      htmlType="button"
                      onClick={() => {
                        setMarkedBuddyId(id);
                        deletePopup.open();
                      }}
                    />
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table>
        )}

        <BuddiesForm className={buildClass('form')} title={t(`${translationBase}.form.title`)}>
          <BuddiesForm.FormikWrapper
            translationBase={`${translationBase}.form`}
            onSubmit={(values, { setSubmitting }) => {
              handleSubmit(values);
              setSubmitting(false);
            }}
          >
            <BuddiesForm.Fields translationBase={`${translationBase}.form`} />
            <BuddiesForm.Submit translationBase={`${translationBase}.form`} className="BuddiesForm__submit" />
          </BuddiesForm.FormikWrapper>
        </BuddiesForm>

        {deletePopup.isOpen && (
          <Popup
            {...deletePopup.popupProps}
            title={t(`${translationBase}.delete.confirmation.title`)}
            closeButtonText={t('buttons.cancel')}
            cta={(
              <Button
                warning
                buttonState={operationStatusDeleteBuddy === UI_OPERATION_STATUS_REQUEST && 'disabled'}
                onClick={() => {
                  deletePopup.close();
                  handleDelete(markedBuddyId);
                }}
              >
                {t('buttons.delete')}
              </Button>
            )}
          >
            {t(`${translationBase}.delete.confirmation.body`)}
          </Popup>
        )}
      </Main>
    </Page>
  );
};

export default PageAccountBuddies;
